import React, { useMemo } from 'react';
import { Select, SelectProps } from 'antd';
import { useAtomValue } from 'jotai';
import { artistOptionsAtom } from './ddex_atoms';
import { artistSelectSearchFieldProps } from './artistSearchUtils';

type TrackArtistSelectProps = Omit<SelectProps<string[]>, 'mode' | 'options'> & {
    /** Main/Featuring: existing artists only. Credits: pick from list or type any name. */
    allowCustom?: boolean;
};

const TrackArtistSelect: React.FC<TrackArtistSelectProps> = ({
    allowCustom = false,
    value,
    onChange,
    disabled,
    placeholder,
    ...selectProps
}) => {
    const artistOptions = useAtomValue(artistOptionsAtom);
    const knownArtistKeys = useMemo(
        () => new Set(artistOptions.map((option) => String(option.value))),
        [artistOptions],
    );

    const handleChange: NonNullable<SelectProps<string[]>['onChange']> = (nextValue, option) => {
        const values = (Array.isArray(nextValue) ? nextValue : [])
            .map((entry) => String(entry).trim())
            .filter(Boolean);

        if (allowCustom) {
            onChange?.(values, option);
            return;
        }

        onChange?.(values.filter((key) => knownArtistKeys.has(key)), option);
    };

    return (
        <Select
            {...selectProps}
            {...artistSelectSearchFieldProps}
            mode={allowCustom ? 'tags' : 'multiple'}
            value={value}
            onChange={handleChange}
            options={artistOptions}
            disabled={disabled}
            placeholder={
                placeholder ??
                (allowCustom ? 'Select or type a name' : 'Select an existing artist')
            }
            notFoundContent={allowCustom ? 'Type a name and press Enter' : 'Select an existing artist'}
        />
    );
};

export default TrackArtistSelect;
